import { useState, useCallback, useEffect } from 'react';
import { useAuth } from './useAuth';
import { chatService } from '../services/chatService';
import { useChatContext, ChatMessage } from '../context/ChatContext';
import { isSessionExpired, getErrorMessageWithHint } from '../utils/errorHandler';
import { logger } from '../utils/logger';
import { sessionDocToStreamEvent } from '../utils/sessionDocStreamEvent';

// Re-export ChatMessage type for backwards compatibility
export type { ChatMessage };

const moduleLogger = logger.getModule('useChat');

interface UseChatReturn {
  messages: ChatMessage[];
  sendMessage: (message: string) => Promise<ChatSendResult | null>;
  updateMessageMetadata: (messageId: string, updates: Record<string, any>) => void;
  isLoading: boolean;
  error: string | null;
  clearMessages: () => void;
  clearError: () => void;
}

const returningWelcomeMessageKeys = [
  'welcome.returning.welcomeBack',
  'welcome.returning.helloAgain',
  'welcome.returning.welcome',
  'welcome.returning.readyToHelp',
  'welcome.returning.driverData',
  'welcome.returning.insights',
  'welcome.returning.explore',
  'welcome.returning.goodToSeeYou',
] as const;

export type WelcomeMessageKey =
  | 'welcome.newUser'
  | (typeof returningWelcomeMessageKeys)[number];

export interface ChatSendResult {
  sourceTurnNumber: number;
  assistantMessageId: string;
}

const parsePositiveInteger = (value: unknown): number | null => {
  const parsedValue = typeof value === 'number'
    ? value
    : Number.parseInt(String(value ?? ''), 10);
  return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : null;
};

export const getWelcomeMessageKey = (isNewUser: boolean): WelcomeMessageKey => {
  if (isNewUser) {
    return 'welcome.newUser';
  }

  const variationIndex = Math.floor(Math.random() * returningWelcomeMessageKeys.length);
  return returningWelcomeMessageKeys[variationIndex] ?? returningWelcomeMessageKeys[0];
};

export const useChat = (): UseChatReturn => {
  const { session, authState, selectedFleetId, selectedFleetName, customerName } = useAuth();
  const {
    messages,
    addMessage,
    updateMessage,
    updateMessageMetadata,
    setMessages,
    clearMessages: clearContextMessages,
    updateSessionId,
  } = useChatContext();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Update session ID in context when it changes
  useEffect(() => {
    updateSessionId(session?.sessionId || null);
  }, [session?.sessionId, updateSessionId]);

  // Add welcome message when session is available and no messages exist
  useEffect(() => {
    if (session?.sessionId && messages.length === 0) {
      moduleLogger.debug('Session ready, adding welcome message', { sessionId: session.sessionId, hasHistory: !!session.history?.length });

      const isNewUser = !session.history || session.history.length === 0;
      const welcomeMessageKey = getWelcomeMessageKey(isNewUser);

      const welcomeMessage: ChatMessage = {
        id: 'welcome-message',
        content: '',
        sender: 'SpeedGauge',
        direction: 'incoming',
        timestamp: new Date().toISOString(),
        localTimestamp: new Date().toLocaleTimeString(),
        metadata: {
          syntheticMessageKey: welcomeMessageKey,
        },
      };
      setMessages([welcomeMessage]);
    }
  }, [session?.sessionId, session?.history, messages.length, setMessages]);

  // Clear error when session changes
  useEffect(() => {
    setError(null);
  }, [session?.sessionId]);

  const clearMessages = useCallback(() => {
    clearContextMessages();
    setError(null);
  }, [clearContextMessages]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const sendMessage = useCallback(async (messageContent: string) => {
    moduleLogger.debug('useChat.sendMessage called', { messageContent });
    moduleLogger.debug('Current session', { session });
    moduleLogger.debug('Auth state', { authState });

    if (!authState.isAuthenticated) {
      moduleLogger.error('Not authenticated');
      setError('Please log in to send messages');
      return null;
    }

    if (!session) {
      // Check if we're authenticated but session is being created
      if (authState.isAuthenticated && authState.user) {
        moduleLogger.error('No session available - session creation may still be in progress');
        setError('Session is being created, please wait a moment and try again');
      } else {
        moduleLogger.error('No session available - not authenticated');
        setError('Please log in to send messages');
      }
      return null;
    }

    if (!messageContent.trim()) {
      moduleLogger.debug('Empty message, ignoring');
      return null;
    }

    // Add user message immediately
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      content: messageContent.trim(),
      sender: authState.user?.username || 'You',
      direction: 'outgoing',
      timestamp: new Date().toISOString(),
      localTimestamp: new Date().toLocaleTimeString()
    };

    moduleLogger.debug('Adding user message', { userMessage });
    addMessage(userMessage);
    setIsLoading(true);
    setError(null);

    try {
      moduleLogger.debug('Sending message to API');
      // Format local timestamp as "Day, MM/DD/YYYY, HH:MM:SS AM/PM"
      const formattedLocalTimestamp = new Date().toLocaleString(undefined, {
        weekday: 'long',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });

      const assistantMessageId = `assistant-${Date.now()}`;
      const assistantMessage: ChatMessage = {
        id: assistantMessageId,
        content: '',
        sender: 'SpeedGauge',
        direction: 'incoming',
        timestamp: new Date().toISOString(),
        localTimestamp: new Date().toLocaleTimeString(),
        metadata: {
          streamEventType: 'response',
          streamPhase: 'update',
          streamEvents: [
            {
              type: 'response',
              phase: 'update',
              timestamp: new Date().toISOString(),
            },
          ],
        },
      };

      addMessage(assistantMessage);

      let streamedContent = '';
      let currentMessageSegment = '';
      const messageSegments: string[] = [];
      let completedSourceTurnNumber: number | null = null;
      const streamEvents: Array<Record<string, any>> = [
        {
          type: 'response',
          phase: 'update',
          timestamp: new Date().toISOString(),
        },
      ];

      const setAssistantStreamingState = (
        updates: {
          content?: string;
          streamEventType?: string;
          streamPhase?: string;
        } = {},
      ) => {
        updateMessage(assistantMessageId, {
          ...(updates.content !== undefined ? { content: updates.content } : {}),
          metadata: {
            streamEventType: updates.streamEventType || 'response',
            streamPhase: updates.streamPhase || 'update',
            streamEvents: [...streamEvents],
            messageSegments: [...messageSegments, currentMessageSegment].filter((segment) => segment.length > 0),
          },
        });
      };

      const flushCurrentMessageSegment = () => {
        if (currentMessageSegment.length === 0) {
          return;
        }

        messageSegments.push(currentMessageSegment);
        currentMessageSegment = '';
      };

      const shouldSeparateContentAfterStreamEvent = (latestEvent: Record<string, any> | undefined, chunk: string): boolean => (
        !!latestEvent &&
        latestEvent.type !== 'update' &&
        streamedContent.length > 0 &&
        chunk.length > 0 &&
        !chunk.startsWith('\n')
      );

      await chatService.sendMessageStream(session.sessionId, messageContent, {
        localTimestamp: formattedLocalTimestamp,
        localTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        fleetId: selectedFleetId, // Include selected fleet ID in message context
        customerName: customerName, // Include customer name in message context
        fleetName: selectedFleetName // Include fleet name in message context
      }, {
        onResponse: () => {
          setAssistantStreamingState({
            streamEventType: 'response',
            streamPhase: 'update',
          });
        },
        onContent: (chunk) => {
          const latestEvent = streamEvents[streamEvents.length - 1];

          if (shouldSeparateContentAfterStreamEvent(latestEvent, chunk)) {
            streamedContent += '\n\n';
            currentMessageSegment += '\n\n';
          }

          streamedContent += chunk;
          currentMessageSegment += chunk;
          const shouldAppendUpdateEvent = !latestEvent || latestEvent.type !== 'update';
          if (shouldAppendUpdateEvent) {
            streamEvents.push({
              type: 'update',
              timestamp: new Date().toISOString(),
            });
          }

          setAssistantStreamingState({
            content: streamedContent,
            streamEventType: 'response',
            streamPhase: 'update',
          });
        },
        onToolUse: (name, input) => {
          flushCurrentMessageSegment();
          streamEvents.push({
            type: 'tool_use',
            toolName: name,
            input: input || {},
            timestamp: new Date().toISOString(),
          });

          setAssistantStreamingState({
            streamEventType: 'response',
            streamPhase: 'update',
          });
        },
        onToolResult: (name, geoData) => {
          streamEvents.push({
            type: 'tool_result',
            toolName: name,
            geoData: geoData || {},
            timestamp: new Date().toISOString(),
          });

          setAssistantStreamingState({
            streamEventType: 'response',
            streamPhase: 'update',
          });
        },
        onReasoning: (reasoning) => {
          if (!reasoning) {
            return;
          }

          const latestEvent = streamEvents[streamEvents.length - 1];
          const shouldAppendReasoning =
            !latestEvent ||
            latestEvent.type !== 'reasoning' ||
            latestEvent.message !== reasoning;

          if (shouldAppendReasoning) {
            streamEvents.push({
              type: 'reasoning',
              message: reasoning,
              timestamp: new Date().toISOString(),
            });

            setAssistantStreamingState({
              streamEventType: 'response',
              streamPhase: 'update',
            });
          }
        },
        onSessionDoc: (payload) => {
          streamEvents.push(sessionDocToStreamEvent(payload));

          setAssistantStreamingState({
            streamEventType: 'response',
            streamPhase: 'update',
          });
        },
        onComplete: (payload) => {
          flushCurrentMessageSegment();
          streamEvents.push({
            type: 'complete',
            timestamp: new Date().toISOString(),
          });

          const parsedDbMessageId = parsePositiveInteger(payload?.db_message_id);
          completedSourceTurnNumber = payload?.status === 'success'
            ? parsePositiveInteger(payload?.turn_number)
            : null;

          setAssistantStreamingState({
            streamEventType: 'response',
            streamPhase: 'complete',
          });

          if (parsedDbMessageId !== null) {
            updateMessage(assistantMessageId, {
              messageId: parsedDbMessageId,
            });
          }
        },
        onError: (streamError) => {
          moduleLogger.error('Streaming error callback', { streamError });
        },
      });
      moduleLogger.debug('Streaming response complete', { contentLength: streamedContent.length });
      return completedSourceTurnNumber
        ? { sourceTurnNumber: completedSourceTurnNumber, assistantMessageId }
        : null;
    } catch (error: any) {
      moduleLogger.error('Failed to send message', error);
      
      // Check if this is a session expiration error
      if (isSessionExpired(error)) {
        moduleLogger.warn('Session has expired due to inactivity');
        const { message, hint } = getErrorMessageWithHint(error);
        
        const errorMessage: ChatMessage = {
          id: `error-${Date.now()}`,
          content: `${message}${hint ? `\n\n💡 **Hint:** ${hint}` : ''}`,
          sender: 'SpeedGauge',
          direction: 'incoming',
          timestamp: new Date().toISOString(),
          localTimestamp: new Date().toLocaleTimeString()
        };

        moduleLogger.debug('Adding session expiration error message', { errorMessage });
        addMessage(errorMessage);
        setError(message);
      } else {
        // Generic error handling
        const { message, hint } = getErrorMessageWithHint(error);
        
        const errorMessage: ChatMessage = {
          id: `error-${Date.now()}`,
          content: `I apologize, but I encountered an error: ${message}${hint ? `\n\n💡 **Hint:** ${hint}` : ''}`,
          sender: 'SpeedGauge',
          direction: 'incoming',
          timestamp: new Date().toISOString(),
          localTimestamp: new Date().toLocaleTimeString()
        };

        moduleLogger.debug('Adding error message', { errorMessage });
        addMessage(errorMessage);
        setError(message);
      }
    } finally {
      moduleLogger.debug('Setting loading to false');
      setIsLoading(false);
    }

    return null;
  }, [session, authState, selectedFleetId, customerName, selectedFleetName, addMessage, updateMessage]);

  return {
    messages,
    sendMessage,
    updateMessageMetadata,
    isLoading,
    error,
    clearMessages,
    clearError
  };
};
